import { Skeleton } from "@/components/ui/skeleton";
import { panelPage, panelSurface } from "@/lib/panel-ui";

const CHART_BAR_HEIGHTS = [35, 48, 42, 60, 52, 70, 85, 62, 78, 92, 82, 96, 88, 92, 75];

export function ProducerDashboardSkeleton() {
  return (
    <div className={panelPage} aria-busy="true" aria-label="Carregando painel do produtor">
      {/* Header com Saudação & Filtro Skeleton */}
      <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
        <div className="space-y-2">
          <Skeleton className="h-8 w-52 sm:w-64 rounded-2xl bg-secondary/80" />
          <Skeleton className="h-4 w-72 sm:w-96 rounded-xl bg-secondary/50" />
        </div>

        <div className="flex flex-wrap items-center gap-2.5">
          <Skeleton className="h-12 w-full sm:w-[320px] rounded-2xl bg-secondary/60 border border-border/40" />
          <Skeleton className="h-12 w-36 rounded-full bg-secondary/70" />
        </div>
      </div>

      {/* Grid de 4 Métricas Principais (Bento Apple) */}
      <div className="grid gap-3 sm:grid-cols-2 xl:grid-cols-4">
        {[
          { labelWidth: "w-24", valWidth: "w-40", subWidth: "w-48" },
          { labelWidth: "w-28", valWidth: "w-28", subWidth: "w-36" },
          { labelWidth: "w-28", valWidth: "w-32", subWidth: "w-40" },
          { labelWidth: "w-20", valWidth: "w-24", subWidth: "w-36" },
        ].map((item, idx) => (
          <div
            key={idx}
            className="flex h-[138px] flex-col justify-between rounded-[28px] border border-border/60 bg-white dark:bg-card p-5 sm:p-6"
          >
            <Skeleton className={`h-3.5 ${item.labelWidth} rounded-lg bg-secondary/70`} />
            <Skeleton className={`h-8 sm:h-9 ${item.valWidth} rounded-xl bg-secondary/80 my-1`} />
            <Skeleton className={`h-3 ${item.subWidth} rounded-lg bg-secondary/50`} />
          </div>
        ))}
      </div>

      {/* Evento em Foco Bento Card */}
      <div className={panelSurface}>
        <div className="border-b border-border/60 px-5 py-4 sm:px-6 flex items-center justify-between">
          <Skeleton className="h-4 w-36 rounded-lg bg-secondary/70" />
        </div>

        <div className="p-5 sm:p-6 space-y-5">
          <div className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between">
            <div className="space-y-2">
              <div className="flex items-center gap-2">
                <Skeleton className="h-5 w-20 rounded-full bg-secondary/60" />
                <Skeleton className="h-5 w-24 rounded-full bg-secondary/50" />
              </div>
              <Skeleton className="h-6 sm:h-7 w-64 sm:w-80 rounded-xl bg-secondary/80" />
              <Skeleton className="h-3.5 w-44 rounded-lg bg-secondary/50" />
            </div>
            <div className="flex items-center gap-2">
              <Skeleton className="h-9 w-28 rounded-full bg-secondary/60" />
              <Skeleton className="h-9 w-9 rounded-full bg-secondary/50" />
            </div>
          </div>

          <div className="grid grid-cols-2 gap-3 sm:grid-cols-4 pt-1">
            {[1, 2, 3, 4].map((i) => (
              <div key={i} className="rounded-2xl bg-secondary/40 p-4 space-y-2">
                <Skeleton className="h-3 w-16 rounded-md bg-secondary/60" />
                <Skeleton className="h-6 w-24 rounded-lg bg-secondary/80" />
                <Skeleton className="h-2.5 w-20 rounded-md bg-secondary/40" />
              </div>
            ))}
          </div>
        </div>
      </div>

      {/* Ritmo de Vendas / Gráfico Bento Card */}
      <div className={panelSurface}>
        <div className="border-b border-border/60 px-5 py-4 sm:px-6 flex flex-col sm:flex-row sm:items-center justify-between gap-3">
          <div className="space-y-1">
            <Skeleton className="h-4 w-32 rounded-lg bg-secondary/70" />
            <Skeleton className="h-3 w-48 rounded-md bg-secondary/50" />
          </div>
          <Skeleton className="h-8 w-56 sm:w-72 rounded-full bg-secondary/50" />
        </div>

        <div className="p-5 sm:p-6">
          <div className="h-64 sm:h-72 rounded-2xl bg-secondary/20 border border-border/40 p-5 flex items-end justify-between gap-2 sm:gap-3">
            {CHART_BAR_HEIGHTS.map((height, i) => (
              <div
                key={i}
                style={{ height: `${height}%` }}
                className="flex-1 rounded-t-lg bg-secondary/50 animate-pulse transition-all duration-300"
              />
            ))}
          </div>
        </div>
      </div>

      {/* Top Eventos Bento Card */}
      <div className={panelSurface}>
        <div className="border-b border-border/60 px-5 py-4 sm:px-6">
          <Skeleton className="h-4 w-44 rounded-lg bg-secondary/70" />
        </div>

        <div className="divide-y divide-border/40">
          {[1, 2, 3].map((i) => (
            <div key={i} className="p-5 sm:px-6 flex items-center justify-between gap-4">
              <div className="flex items-center gap-3.5 min-w-0">
                <Skeleton className="h-10 w-10 shrink-0 rounded-xl bg-secondary/70" />
                <div className="space-y-1.5 min-w-0">
                  <Skeleton className="h-4 w-40 sm:w-60 rounded-lg bg-secondary/80" />
                  <Skeleton className="h-3 w-28 rounded-md bg-secondary/50" />
                </div>
              </div>
              <div className="flex items-center gap-4 sm:gap-8 shrink-0">
                <Skeleton className="h-4 w-20 rounded-md bg-secondary/60 hidden sm:block" />
                <Skeleton className="h-5 w-24 rounded-lg bg-secondary/80" />
              </div>
            </div>
          ))}
        </div>
      </div>
    </div>
  );
}
